/** Темы панели: список, применение, запоминание выбора и всплывающий выбор. */
import { h, icon } from './dom.js';
import { popover } from './ui/overlay.js';

export interface ThemeInfo {
  id: string;
  name: string;
  hint: string;
  light: boolean;
  /** фон и два акцента для мини-превью */
  swatch: [string, string, string];
}

export const THEMES: ThemeInfo[] = [
  { id: 'aurora', name: 'Аврора', hint: 'Фиолетово-синее стекло', light: false, swatch: ['#0c0d26', '#8f7dff', '#45d3ff'] },
  { id: 'midnight', name: 'Полночь', hint: 'Тёмная, почти чёрная', light: false, swatch: ['#050507', '#8b9cff', '#6fd6ff'] },
  { id: 'graphite', name: 'Графит', hint: 'Нейтральная серая', light: false, swatch: ['#1c1d22', '#e3e5ea', '#9db4d6'] },
  { id: 'ocean', name: 'Океан', hint: 'Бирюза и глубина', light: false, swatch: ['#03161f', '#2fd0c0', '#5aa8ff'] },
  { id: 'sunset', name: 'Закат', hint: 'Тёплые оранжевые тона', light: false, swatch: ['#1b0915', '#ff4f8b', '#ffb347'] },
  { id: 'moss', name: 'Мох', hint: 'Зелёная, спокойная', light: false, swatch: ['#08140c', '#6fdc8c', '#b6e36a'] },
  { id: 'frost', name: 'Иней', hint: 'Светлая', light: true, swatch: ['#eef1f8', '#7a5cff', '#1aa7e8'] },
  { id: 'sakura', name: 'Сакура', hint: 'Светлая, розовая', light: true, swatch: ['#fbf0f3', '#e04c8c', '#f29a4a'] },
];

const KEY = 'es:ui-theme';
/** 'system' - светлая или тёмная по настройкам системы */
type Choice = string;

const listeners = new Set<(t: ThemeInfo) => void>();
const media = window.matchMedia?.('(prefers-color-scheme: light)');

function readChoice(): Choice {
  try {
    return localStorage.getItem(KEY) || 'aurora';
  } catch {
    return 'aurora';
  }
}

function resolve(choice: Choice): ThemeInfo {
  if (choice === 'system') return THEMES.find((t) => t.id === (media?.matches ? 'frost' : 'midnight'))!;
  return THEMES.find((t) => t.id === choice) ?? THEMES[0];
}

let choice = readChoice();
let current = resolve(choice);

function apply() {
  current = resolve(choice);
  const root = document.documentElement;
  if (current.id === 'aurora') root.removeAttribute('data-ui');
  else root.setAttribute('data-ui', current.id);
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', current.swatch[0]);
  for (const l of listeners) l(current);
}

export function initTheme() {
  apply();
  media?.addEventListener?.('change', () => {
    if (choice === 'system') apply();
  });
}

export function currentTheme(): ThemeInfo {
  return current;
}

export function onThemeChange(fn: (t: ThemeInfo) => void) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function setTheme(next: Choice) {
  choice = next;
  try {
    localStorage.setItem(KEY, next);
  } catch {
    /* хранилище недоступно - тема действует до перезагрузки */
  }
  apply();
}

function themeCard(t: ThemeInfo | null, onPick: () => void): HTMLElement {
  const id = t?.id ?? 'system';
  const active = choice === id;
  const preview = t
    ? h(
        'span',
        { class: 'theme-preview', style: { background: t.swatch[0] } },
        h('span', { class: 'theme-preview-bar', style: { background: `linear-gradient(135deg, ${t.swatch[1]}, ${t.swatch[2]})` } }),
        h('span', { class: ['theme-preview-line', t.light && 'on-light'] }),
        h('span', { class: ['theme-preview-line', 'short', t.light && 'on-light'] }),
      )
    : h(
        'span',
        { class: 'theme-preview theme-preview-split' },
        h('span', { style: { background: '#eef1f8' } }),
        h('span', { style: { background: '#050507' } }),
      );
  return h(
    'button',
    { class: ['theme-card', active && 'active'], type: 'button', attrs: { 'aria-pressed': active }, onclick: onPick },
    preview,
    h('span', { class: 'theme-name' }, t?.name ?? 'Как в системе', active ? icon('check', 13) : null),
    h('span', { class: 'theme-hint' }, t?.hint ?? 'Иней днём, Полночь ночью'),
  );
}

export function openThemePicker(anchor: HTMLElement) {
  const grid = h('div', { class: 'theme-grid' });
  const render = () => {
    grid.replaceChildren(
      ...THEMES.map((t) =>
        themeCard(t, () => {
          setTheme(t.id);
          render();
        }),
      ),
      themeCard(null, () => {
        setTheme('system');
        render();
      }),
    );
  };
  render();
  popover(anchor, h('div', { class: 'picker' }, h('div', { class: 'picker-section' }, 'Тема панели'), grid, h('p', { class: 'muted small theme-note' }, 'Предпросмотр Discord переключается отдельно кнопкой в его заголовке.')), {
    width: 360,
  });
}

export function themeButton(): HTMLButtonElement {
  const btn: HTMLButtonElement = h(
    'button',
    { class: 'icon-btn', type: 'button', title: 'Тема панели', attrs: { 'aria-label': 'Тема панели' }, onclick: () => openThemePicker(btn) },
    icon('palette', 16),
  );
  return btn;
}
